/* ─────────────────────────────────────────────────────────────
   AriZona Store Locator – PRODUCT PAGE version
   • No map — two scrollable lists (Stores | Buy Online)
   • Compact ~400px height, list scrolls internally
   • Auto-searches on load if ZIP was saved from a prior visit
   • Same backend as the full locator
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const BACKEND_URL = 'https://vip-store-locator-api.onrender.com';

  const LIST_PAGE_SIZE = 8;        // results shown per page in the list
  const API_PAGE_SIZE = 400;       // results pulled from API per search
  const ZIP_STORAGE_KEY = 'arizona_locator_zip';
  const RADIUS_STORAGE_KEY = 'arizona_locator_radius';

  // ── Online retailers (Buy Online tab) ──────────────────────
  // Keep this list in sync with store-locator-tabs.js when you edit it.
  // Same shape: id, name, tagline, url, monogram, optional logo URL.
  const RETAILERS = [
    {
      id: 'totalwine',
      name: 'Total Wine',
      tagline: 'Ship or pickup',
      url: 'https://www.totalwine.com/beer/brand/arizona-hard-tea',
      monogram: { letters: 'TW', bg: '#002E5D', fg: '#ffffff' },
    },
    {
      id: 'bevmo',
      name: 'BevMo!',
      tagline: 'Ship or pickup',
      url: 'https://bevmo.com/search?q=arizona+hard',
      monogram: { letters: 'B!', bg: '#003DA5', fg: '#ffffff' },
    },
    {
      id: 'instacart',
      name: 'Instacart',
      tagline: 'Same-day delivery',
      url: 'https://www.instacart.com/store/s?k=arizona+hard+tea',
      monogram: { letters: 'i', bg: '#43B02A', fg: '#ffffff' },
    },
    {
      id: 'target',
      name: 'Target',
      tagline: 'Pickup or delivery',
      url: 'https://www.target.com/b/arizona-hard-tea/-/N-q643leazbys',
      monogram: { letters: 'T', bg: '#CC0000', fg: '#ffffff' },
    },
    {
      id: 'safeway',
      name: 'Safeway',
      tagline: 'Pickup or delivery',
      url: 'https://www.safeway.com/shop/search-results.html?q=arizona+hard+tea',
      monogram: { letters: 'S', bg: '#E11A2C', fg: '#ffffff' },
    },
    {
      id: 'gopuff',
      name: 'Gopuff',
      tagline: 'Fast local delivery',
      url: 'https://www.gopuff.com/go/search?query=arizona+hard',
      monogram: { letters: 'G', bg: '#7B2CBF', fg: '#ffffff' },
    },
    {
      id: 'minibar',
      name: 'Minibar',
      tagline: 'Local delivery',
      url: 'https://minibardelivery.com/store/search?q=arizona+hard',
      monogram: { letters: 'M', bg: '#FFD700', fg: '#1a1a1a' },
    },
    {
      id: 'ubereats',
      name: 'Uber Eats',
      tagline: 'On-demand delivery',
      url: 'https://www.ubereats.com/search?q=arizona%20hard%20tea',
      monogram: { letters: 'U', bg: '#000000', fg: '#06C167' },
    },
  ];

  // ── DOM refs ───────────────────────────────────────────────
  const locatorEl   = document.getElementById('storeLocatorProduct');
  if (!locatorEl) return;   // block isn't on this page; bail silently

  const form        = locatorEl.querySelector('#searchFormP');
  const zipInput    = locatorEl.querySelector('#zipInputP');
  const milesInput  = locatorEl.querySelector('#milesInputP');
  const searchBtn   = locatorEl.querySelector('#searchBtnP');
  const status      = locatorEl.querySelector('#statusAreaP');
  const resultsList = locatorEl.querySelector('#resultsListP');
  const onlineGrid  = locatorEl.querySelector('#onlineGridP');
  const tabsEl      = locatorEl.querySelector('#locatorTabsP');
  const pagination  = locatorEl.querySelector('#paginationP');
  const prevBtn     = locatorEl.querySelector('#prevPageBtnP');
  const nextBtn     = locatorEl.querySelector('#nextPageBtnP');
  const pageInfo    = locatorEl.querySelector('#pageInfoP');

  // ── State ──────────────────────────────────────────────────
  let currentResults = [];
  let currentListPage = 0;
  let currentTab = 'stores';

  // ── Tab switching ──────────────────────────────────────────
  function setTab(tab) {
    if (tab !== 'stores' && tab !== 'online') tab = 'stores';
    currentTab = tab;
    locatorEl.dataset.tab = tab;

    if (tabsEl) {
      tabsEl.querySelectorAll('.locator-tab').forEach(btn => {
        const isActive = btn.dataset.tab === tab;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    // Render the online grid lazily on first switch
    if (tab === 'online' && onlineGrid && !onlineGrid.dataset.rendered) {
      renderRetailers();
      onlineGrid.dataset.rendered = 'true';
    }
  }

  // ── Render Buy Online retailer grid (one-time) ─────────────
  function renderRetailers() {
    if (!onlineGrid) return;

    onlineGrid.innerHTML = RETAILERS.map(r => {
      const visual = r.logo
        ? `<img class="retailer-card__logo" src="${escapeAttr(r.logo)}" alt="${escapeAttr(r.name)} logo" loading="lazy">`
        : `<span class="retailer-card__monogram"
                 style="background:${escapeAttr(r.monogram.bg)};color:${escapeAttr(r.monogram.fg)};"
                 aria-hidden="true">${escapeHtml(r.monogram.letters)}</span>`;

      return `
        <a class="retailer-card"
           href="${escapeAttr(r.url)}"
           target="_blank"
           rel="noopener noreferrer"
           data-retailer-id="${escapeAttr(r.id)}">
          ${visual}
          <span class="retailer-card__text">
            <span class="retailer-card__name">${escapeHtml(r.name)}</span>
            <span class="retailer-card__tagline">${escapeHtml(r.tagline)}</span>
          </span>
        </a>
      `;
    }).join('');
  }

  // ── ZIP persistence ────────────────────────────────────────
  // Saves last-used ZIP + radius after a successful search so the next
  // product page the user lands on can auto-populate and auto-search.
  function saveLastSearch(zip, miles) {
    try {
      localStorage.setItem(ZIP_STORAGE_KEY, zip);
      localStorage.setItem(RADIUS_STORAGE_KEY, miles);
    } catch (e) { /* private mode / quota — non-fatal */ }
  }
  function loadLastSearch() {
    try {
      return {
        zip: localStorage.getItem(ZIP_STORAGE_KEY) || '',
        miles: localStorage.getItem(RADIUS_STORAGE_KEY) || '',
      };
    } catch (e) {
      return { zip: '', miles: '' };
    }
  }

  // ── Search ─────────────────────────────────────────────────
  async function doSearch() {
    if (currentTab !== 'stores') setTab('stores');

    const zip = zipInput.value.trim();
    if (!/^\d{5}$/.test(zip)) {
      showError('Please enter a valid 5-digit ZIP code.');
      zipInput.focus();
      return;
    }

    const miles = milesInput.value;
    const params = new URLSearchParams({
      zip,
      miles,
      page: '0',
      pagesize: String(API_PAGE_SIZE),
    });

    setLoading(true);
    showStatus('Searching…');

    try {
      const res = await fetch(`${BACKEND_URL}/api/results?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      currentResults = data.locations || [];
      currentListPage = 0;
      renderListPage();

      if (currentResults.length) {
        // Only persist on a successful search with results
        saveLastSearch(zip, miles);
      } else {
        showStatus('No stores found in this area. Try widening your radius.');
      }
    } catch (err) {
      console.error(err);
      showError(`Search failed: ${err.message}`);
      resultsList.innerHTML = '';
      if (pagination) pagination.hidden = true;
    } finally {
      setLoading(false);
    }
  }

  // ── Render list (no map, so this is the full results UI) ───
  function renderListPage() {
    resultsList.innerHTML = '';

    if (!currentResults.length) {
      if (pagination) pagination.hidden = true;
      return;
    }

    const total = currentResults.length;
    const totalListPages = Math.ceil(total / LIST_PAGE_SIZE);
    const startIdx = currentListPage * LIST_PAGE_SIZE;
    const endIdx = Math.min(startIdx + LIST_PAGE_SIZE, total);
    const slice = currentResults.slice(startIdx, endIdx);

    showStatus(`Showing ${startIdx + 1}–${endIdx} of ${total} store${total === 1 ? '' : 's'}`);

    slice.forEach((loc, n) => {
      const i = startIdx + n;
      const li = document.createElement('li');
      li.className = 'result-card';
      li.dataset.idx = i;

      const rollup = (loc.storeTypeRollup || '').toLowerCase();
      let typeTag = '';
      if (rollup === 'on') typeTag = '<span class="tag">Bar / Restaurant</span>';
      else if (rollup === 'off') typeTag = '<span class="tag">Retail</span>';

      const phoneHtml = loc.phoneFormatted
        ? `<span class="result-card__phone">${escapeHtml(loc.phoneFormatted)}</span>`
        : '';

      const fullAddress = [loc.street, loc.city, loc.state, loc.zip]
        .filter(Boolean).join(', ');
      const mapsUrl = 'https://www.google.com/maps/search/?api=1&query=' +
        encodeURIComponent(`${loc.dba || ''} ${fullAddress}`.trim());

      li.innerHTML = `
        <div class="result-card__head">
          <a href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener"
             class="result-card__head-text result-card__link"
             title="Open in Google Maps">
            <p class="result-card__name">${escapeHtml(loc.dba || 'Unnamed location')}</p>
            <p class="result-card__addr">
              ${escapeHtml(loc.street || '')}<br>
              ${escapeHtml(loc.city || '')}, ${escapeHtml(loc.state || '')} ${escapeHtml(loc.zip || '')}
            </p>
          </a>
          <span class="result-card__distance">${escapeHtml(String(loc.distance || '?'))} mi</span>
        </div>
        ${(typeTag || phoneHtml) ? `<div class="result-card__meta">${typeTag}${phoneHtml}</div>` : ''}
      `;
      resultsList.appendChild(li);
    });

    if (pagination && pageInfo && prevBtn && nextBtn) {
      if (totalListPages > 1) {
        pagination.hidden = false;
        pageInfo.textContent = `Page ${currentListPage + 1} of ${totalListPages}`;
        prevBtn.disabled = currentListPage === 0;
        nextBtn.disabled = currentListPage >= totalListPages - 1;
      } else {
        pagination.hidden = true;
      }
    }

    resultsList.scrollTop = 0;
  }

  // ── UI helpers ─────────────────────────────────────────────
  function setLoading(isLoading) {
    searchBtn.disabled = isLoading;
    searchBtn.classList.toggle('btn--loading', isLoading);
  }
  function showStatus(msg) {
    status.classList.remove('is-error');
    status.textContent = msg;
  }
  function showError(msg) {
    status.classList.add('is-error');
    status.textContent = msg;
  }
  function escapeHtml(s) {
    const decoded = decodeEntities(String(s));
    return decoded.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  let _decoder;
  function decodeEntities(s) {
    if (!_decoder) _decoder = document.createElement('textarea');
    _decoder.innerHTML = s;
    return _decoder.value;
  }

  // ── Event wiring ───────────────────────────────────────────
  form.addEventListener('submit', e => {
    e.preventDefault();
    doSearch();
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (currentListPage > 0) {
        currentListPage--;
        renderListPage();
      }
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const totalListPages = Math.ceil(currentResults.length / LIST_PAGE_SIZE);
      if (currentListPage < totalListPages - 1) {
        currentListPage++;
        renderListPage();
      }
    });
  }

  if (tabsEl) {
    tabsEl.addEventListener('click', e => {
      const btn = e.target.closest('.locator-tab');
      if (!btn) return;
      setTab(btn.dataset.tab);
    });
    tabsEl.addEventListener('keydown', e => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tabs = Array.from(tabsEl.querySelectorAll('.locator-tab'));
      const idx = tabs.findIndex(t => t === document.activeElement);
      if (idx === -1) return;
      const next = e.key === 'ArrowRight'
        ? tabs[(idx + 1) % tabs.length]
        : tabs[(idx - 1 + tabs.length) % tabs.length];
      next.focus();
      setTab(next.dataset.tab);
    });
  }

  // ── Init ───────────────────────────────────────────────────
  function init() {
    setTab('stores');

    // Restore prior ZIP and auto-search if we have one.
    // Slight delay so back-to-back product page nav doesn't hammer the API
    // before the user has actually settled on the page.
    const saved = loadLastSearch();
    if (saved.zip) {
      zipInput.value = saved.zip;
      if (saved.miles) {
        // Only set if option exists; otherwise leave the default (25)
        const opt = milesInput.querySelector(`option[value="${saved.miles}"]`);
        if (opt) milesInput.value = saved.miles;
      }
      setTimeout(() => {
        if (/^\d{5}$/.test(zipInput.value.trim())) doSearch();
      }, 400);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
